import { useState, useEffect, useRef } from "react";
import axios, { API } from "../../api";
import { SnapShareButton } from "../SnapShareButton";

/**
 * Helper: trigger a browser download of a base64 image.
 * Returns true on success.
 */
export function downloadWallpaper(imageBase64, filename = "aura-wallpaper.png") {
  try {
    const dataUrl = imageBase64.startsWith("data:")
      ? imageBase64
      : `data:image/png;base64,${imageBase64}`;
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } catch (e) {
    console.error("download failed", e);
    return false;
  }
}

/**
 * Lazy-loading tile image. Only fetches the wallpaper's image bytes from the
 * backend when the tile scrolls into view (IntersectionObserver). Keeps the
 * initial gallery load under a few hundred KB no matter how many wallpapers
 * the user has accumulated.
 */
function LazyWallpaperImage({ wallpaperId, cachedBase64, onLoaded, alt }) {
  const [src, setSrc] = useState(cachedBase64 || null);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (src || !ref.current) return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !src && !loading) {
          setLoading(true);
          axios.get(`${API}/wallpaper/${wallpaperId}/image`)
            .then((res) => {
              const b64 = res.data?.image_base64;
              if (b64) {
                const dataUrl = `data:image/png;base64,${b64}`;
                setSrc(dataUrl);
                onLoaded?.(b64);
              }
            })
            .catch(() => { /* tile shows placeholder on error */ })
            .finally(() => setLoading(false));
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // start loading slightly before they're visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [wallpaperId, src, loading, onLoaded]);

  return (
    <div ref={ref} className="gallery-thumb-wrap">
      {src
        ? <img src={src} alt={alt} className="gallery-thumb" loading="lazy" />
        : <div className="gallery-thumb-placeholder">{loading ? "✨" : ""}</div>
      }
    </div>
  );
}

/**
 * Bucket a wallpaper's generated_at timestamp into one of:
 * "today" | "yesterday" | "thisWeek" | "thisMonth" | "earlier-<YYYY>-<MM>" | "earlier-<YYYY>"
 *
 * As the user accumulates wallpapers we collapse older time-windows into
 * folders so the gallery doesn't try to render hundreds of tiles at once.
 */
function bucketKey(generatedAt, now = new Date()) {
  const t = new Date(generatedAt);
  if (isNaN(t.getTime())) return "earlier";
  const msInDay = 24 * 60 * 60 * 1000;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const tStart = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  const daysAgo = Math.floor((startOfToday - tStart) / msInDay);
  if (daysAgo <= 0) return "today";
  if (daysAgo === 1) return "yesterday";
  if (daysAgo < 7) return "thisWeek";
  if (t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth()) return "thisMonth";
  if (t.getFullYear() === now.getFullYear()) {
    const monthName = t.toLocaleString(undefined, { month: "long" });
    return `earlier-${t.getFullYear()}-${String(t.getMonth()).padStart(2, "0")}|${monthName} ${t.getFullYear()}`;
  }
  return `year-${t.getFullYear()}|${t.getFullYear()}`;
}

function bucketLabel(key) {
  if (key === "today") return "Today";
  if (key === "yesterday") return "Yesterday";
  if (key === "thisWeek") return "This Week";
  if (key === "thisMonth") return "This Month";
  // earlier-YYYY-MM|Month YYYY  → grab the label after |
  if (key.includes("|")) return key.split("|")[1];
  return "Earlier";
}

const BUCKET_ORDER = ["today", "yesterday", "thisWeek", "thisMonth"];

function groupByBucket(wallpapers) {
  const now = new Date();
  const groups = new Map();
  for (const w of wallpapers) {
    const key = bucketKey(w.generated_at, now);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(w);
  }
  // Sort group order: recent fixed buckets first, then descending by date for older folders
  const orderedKeys = [
    ...BUCKET_ORDER.filter(k => groups.has(k)),
    ...Array.from(groups.keys())
      .filter(k => !BUCKET_ORDER.includes(k))
      .sort((a, b) => b.localeCompare(a)) // year-2025 > year-2024, earlier-2026-01 > earlier-2025-12
  ];
  return orderedKeys.map(k => ({ key: k, label: bucketLabel(k), items: groups.get(k) }));
}

/**
 * Modal: full wallpaper gallery for the logged-in user.
 * Lets you favorite, download, delete, or re-set as active any past wallpaper.
 *
 * Now uses metadata-only gallery loading + lazy-loaded image tiles +
 * time-bucketed folder groupings so it stays fast as wallpapers pile up.
 */
export function WallpaperGalleryModal({ onClose, onSetActive }) {
  const [wallpapers, setWallpapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null); // wallpaper obj to show fullscreen
  const [busyId, setBusyId] = useState(null);
  // Track which older folders are open. Today + Yesterday + This Week + This Month
  // are open by default; older folders start collapsed.
  const [openBuckets, setOpenBuckets] = useState(() => new Set(BUCKET_ORDER));
  // Cache of lazy-loaded image data, keyed by wallpaper id. Used so once a tile
  // has loaded its image, subsequent operations (download, share, etc) don't
  // need to re-fetch it.
  const [imageCache, setImageCache] = useState({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/wallpaper/history`, {
        params: { limit: 200, favorites_only: favoritesOnly },
      });
      setWallpapers(res.data.wallpapers || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [favoritesOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 30.V — Prefetch share-ready files on touch/mousedown so
  // `navigator.share()` can be called SYNCHRONOUSLY inside the subsequent
  // click handler. iOS Safari (and recent Chrome) revoke the user-gesture
  // token if anything `await`s between the click and `navigator.share()`,
  // which is what produced "Failed to execute 'share' on 'Navigator': Must
  // be handling a user gesture..." in production.
  const prefetchedFiles = useRef({}); // { [wallpaperId]: File }
  const prefetchInflight = useRef({}); // { [wallpaperId]: Promise }

  const _b64ToFile = (b64, filename) => {
    const byteString = atob(b64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    return new File([bytes], filename, { type: "image/png" });
  };

  const prefetchShareFile = (w, filenamePrefix = "aura") => {
    if (!w || !w.id) return;
    if (prefetchedFiles.current[w.id]) return;
    if (prefetchInflight.current[w.id]) return;
    const p = axios.get(`${API}/wallpaper/${w.id}/social-image`)
      .then((res) => {
        const b64 = res.data?.image_base64;
        if (b64) {
          prefetchedFiles.current[w.id] = _b64ToFile(
            b64, `${filenamePrefix}-${(w.id || "").slice(0, 6)}.png`,
          );
        }
      })
      .catch(() => { /* swallow — will fall back to download */ })
      .finally(() => { delete prefetchInflight.current[w.id]; });
    prefetchInflight.current[w.id] = p;
  };

  const toggleFavorite = async (w) => {
    setBusyId(w.id);
    try {
      const res = await axios.post(`${API}/wallpaper/${w.id}/favorite`);
      setWallpapers((list) => list.map((x) => x.id === w.id ? { ...x, is_favorite: res.data.is_favorite } : x));
    } catch (e) { /* noop */ } finally { setBusyId(null); }
  };

  const handleDelete = async (w) => {
    if (!window.confirm("Delete this wallpaper? It's gone for good.")) return;
    setBusyId(w.id);
    try {
      await axios.delete(`${API}/wallpaper/${w.id}`);
      setWallpapers((list) => list.filter((x) => x.id !== w.id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete");
    } finally { setBusyId(null); }
  };

  // For actions that need the actual image bytes (download/setActive/share),
  // pull from cache if loaded, otherwise fetch on demand.
  const getImageBase64 = async (w) => {
    if (imageCache[w.id]) return imageCache[w.id];
    const res = await axios.get(`${API}/wallpaper/${w.id}/image`);
    const b64 = res.data?.image_base64;
    if (b64) setImageCache(c => ({ ...c, [w.id]: b64 }));
    return b64;
  };

  const handleSetActive = async (w) => {
    setBusyId(w.id);
    try {
      const b64 = await getImageBase64(w);
      await axios.post(`${API}/wallpaper/${w.id}/set-active`);
      onSetActive?.(b64);
      onClose();
    } catch (e) { /* noop */ } finally { setBusyId(null); }
  };

  const handleDownload = async (w) => {
    setBusyId(w.id);
    try {
      const b64 = await getImageBase64(w);
      if (b64) downloadWallpaper(b64, `aura-${w.mood || "wallpaper"}-${(w.id || "").slice(0, 6)}.png`);
    } finally { setBusyId(null); }
  };

  const toggleBucket = (key) => {
    setOpenBuckets(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Fetches a watermarked ("✦ Aura AI") copy of the wallpaper for posting to
  // social. The original /download stays clean — only the social-bound copy is
  // stamped. Handles AI-content disclosure rules + doubles as organic marketing.
  const handleShareToSocial = async (w) => {
    setBusyId(w.id);
    try {
      const res = await axios.get(`${API}/wallpaper/${w.id}/social-image`);
      const b64 = res.data?.image_base64;
      if (!b64) throw new Error("no image");
      downloadWallpaper(b64, `aura-social-${(w.id || "").slice(0, 6)}.png`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't prepare the share-ready version. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  // Native share to Snap/IG/X/etc via the OS share sheet. Works on iOS + Android
  // and modern desktop. We share the WATERMARKED version (✦ Aura AI baked in)
  // and let the OS surface every app the user has installed — Snap shows up
  // naturally without needing Snap's Creative Kit SDK or a separate Snap app
  // registration. If the share API isn't available, fall back to download.
  //
  // Phase 30.V — Synchronous-share fix. `navigator.share()` MUST run inside
  // the user gesture (no `await` before it on iOS Safari). We rely on
  // `prefetchShareFile()` having been triggered by onTouchStart/onMouseDown.
  const handleNativeShare = (w) => {
    const file = prefetchedFiles.current[w.id];
    if (file && typeof navigator !== "undefined" && navigator.canShare
        && navigator.canShare({ files: [file] })) {
      // Call SYNCHRONOUSLY — no `await` between click handler entry and share().
      navigator.share({
        files: [file],
        title: "Made with Aura AI",
        text: "✦ Aura AI · aura.wwwwhynot.com",
      }).catch((e) => {
        if (e?.name !== "AbortError") alert(e?.message || "Share didn't go through.");
      });
      return;
    }

    // File wasn't ready in time (slow network, or share button tapped without
    // touchstart firing first). Fall back to download — gives the user the
    // same outcome: a watermarked image they can post.
    setBusyId(w.id);
    axios.get(`${API}/wallpaper/${w.id}/social-image`).then((res) => {
      const b64 = res.data?.image_base64;
      if (!b64) throw new Error("no image");
      const f = _b64ToFile(b64, `aura-${(w.id || "").slice(0, 6)}.png`);
      prefetchedFiles.current[w.id] = f;
      const url = URL.createObjectURL(f);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(url);
      alert("Image downloaded — tap share again and your OS share sheet will open instantly.");
    }).catch((e) => {
      alert(e?.response?.data?.detail || e?.message || "Couldn't prepare the share image.");
    }).finally(() => { setBusyId(null); });
  };

  // Post a text-only tweet directly via our /api/x/tweet endpoint. Pre-fills the
  // text with a wallpaper-themed default the user can edit before sending. To
  // attach the actual wallpaper image, users should use the 📤 share button —
  // X's media-upload API requires multi-step chunked uploads we haven't wired.
  const handlePostToX = async (w) => {
    const defaultText = `Made this with ✦ Aura AI ✨\n\naura.wwwwhynot.com`;
    const tweetText = window.prompt(
      "Post to X — what should it say?\n(280 char limit. Want the image too? Use the share button instead.)",
      defaultText
    );
    if (!tweetText || !tweetText.trim()) return;
    if (tweetText.length > 280) {
      alert(`That's ${tweetText.length} characters — X's limit is 280. Trim it down and try again.`);
      return;
    }
    setBusyId(w.id);
    try {
      const res = await axios.post(`${API}/x/tweet`, { text: tweetText.trim() });
      const tweetId = res.data?.tweet?.id;
      if (tweetId) {
        if (window.confirm(`Posted to X! Open it now?`)) {
          window.open(`https://x.com/i/web/status/${tweetId}`, "_blank");
        }
      } else {
        alert("Posted to X!");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "X account not connected") {
        alert("Connect X first from Settings → Connected Accounts, then try again.");
      } else {
        alert(detail || "Couldn't post to X right now. Try again in a moment.");
      }
    } finally {
      setBusyId(null);
    }
  };

  // Snap-optimized share. On mobile, OS share sheet surfaces Snapchat directly
  // — Snap accepts the image and opens straight into their Story camera with
  // it pre-loaded. On desktop, falls back to a download + instructions.
  // Snap Creative Kit's web SDK requires per-app review by Snap; native share
  // gets us 90% of the same value without that approval gate.
  // Phase 30.V — Snap share via OS native sheet, synchronous.
  const handleShareToSnap = (w) => {
    const file = prefetchedFiles.current[w.id];
    if (file && typeof navigator !== "undefined" && navigator.canShare
        && navigator.canShare({ files: [file] })) {
      navigator.share({
        files: [file],
        title: "Sent from ✦ Aura",
        text: "✦ Made with Aura · aura.wwwwhynot.com",
      }).catch((e) => {
        if (e?.name !== "AbortError") alert(e?.message || "Snap share didn't go through.");
      });
      return;
    }

    // Fallback: download + open Snap web.
    setBusyId(w.id);
    axios.get(`${API}/wallpaper/${w.id}/social-image`).then((res) => {
      const b64 = res.data?.image_base64;
      if (!b64) throw new Error("no image");
      const f = _b64ToFile(b64, `aura-snap-${(w.id || "").slice(0, 6)}.png`);
      prefetchedFiles.current[w.id] = f;
      const url = URL.createObjectURL(f);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(url);
      if (window.confirm(
        "Image downloaded — tap share again and the share sheet will open instantly. " +
        "Or open Snap web now?"
      )) {
        window.open("https://web.snapchat.com", "_blank");
      }
    }).catch((e) => {
      alert(e?.response?.data?.detail || e?.message || "Couldn't open Snap share.");
    }).finally(() => { setBusyId(null); });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="gallery-modal" onClick={(e) => e.stopPropagation()} data-testid="gallery-modal">
        <div className="journal-header">
          <h2>🖼️ My Wallpapers</h2>
          <button className="journal-close" onClick={onClose} data-testid="gallery-close">×</button>
        </div>

        <div className="gallery-tabs">
          <button
            className={`gallery-tab ${!favoritesOnly ? "active" : ""}`}
            onClick={() => setFavoritesOnly(false)}
            data-testid="gallery-tab-all"
          >All</button>
          <button
            className={`gallery-tab ${favoritesOnly ? "active" : ""}`}
            onClick={() => setFavoritesOnly(true)}
            data-testid="gallery-tab-favorites"
          >❤️ Favorites</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <p style={{ opacity: 0.7, textAlign: "center", padding: "1rem" }}>Loading...</p>
        ) : wallpapers.length === 0 ? (
          <div className="gallery-empty">
            <p>🌸</p>
            <p>{favoritesOnly ? "No favorites yet. Heart a wallpaper to save it here." : "No wallpapers yet — they'll appear as Aura generates them."}</p>
          </div>
        ) : (
          <div className="gallery-buckets">
            {groupByBucket(wallpapers).map(({ key, label, items }) => {
              const open = openBuckets.has(key);
              const isRecent = BUCKET_ORDER.includes(key);
              return (
                <div key={key} className={`gallery-bucket ${open ? "open" : "collapsed"}`} data-testid={`gallery-bucket-${key}`}>
                  <button
                    className="gallery-bucket-header"
                    onClick={() => toggleBucket(key)}
                    data-testid={`gallery-bucket-toggle-${key}`}
                  >
                    <span className="gallery-bucket-arrow">{open ? "▾" : "▸"}</span>
                    <span className="gallery-bucket-label">
                      {isRecent ? "" : "📁 "}{label}
                    </span>
                    <span className="gallery-bucket-count">{items.length}</span>
                  </button>
                  {open && (
                    <div className="gallery-grid">
                      {items.map((w) => {
                        // "Active" badge only on the very first wallpaper in the very
                        // first bucket of the All view.
                        const isActive =
                          !favoritesOnly &&
                          key === "today" &&
                          items[0]?.id === w.id &&
                          wallpapers[0]?.id === w.id;
                        return (
                          <div
                            key={w.id}
                            className={`gallery-card ${isActive ? "active" : ""}`}
                            data-testid={`gallery-card-${w.id}`}
                          >
                            <button
                              className="gallery-thumb"
                              onClick={() => setPreview(w)}
                              aria-label="Preview"
                              data-testid={`gallery-thumb-${w.id}`}
                            >
                              <LazyWallpaperImage
                                wallpaperId={w.id}
                                cachedBase64={imageCache[w.id] ? `data:image/png;base64,${imageCache[w.id]}` : null}
                                alt={w.mood || "wallpaper"}
                                onLoaded={(b64) => setImageCache(c => ({ ...c, [w.id]: b64 }))}
                              />
                              {isActive && <span className="gallery-active-badge">Active</span>}
                              {w.mood && <span className="gallery-mood-badge">{w.mood}</span>}
                            </button>
                            <div className="gallery-card-actions">
                              <button
                                className="gallery-action gallery-action-outline"
                                onClick={() => toggleFavorite(w)}
                                disabled={busyId === w.id}
                                title={w.is_favorite ? "Unfavorite" : "Favorite"}
                                data-testid={`gallery-fav-${w.id}`}
                                aria-label={w.is_favorite ? "Unfavorite" : "Favorite"}
                              >
                                <svg viewBox="0 0 24 24" fill={w.is_favorite ? "url(#beta-gradient-stroke)" : "none"} stroke="url(#beta-gradient-stroke)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                </svg>
                              </button>
                              <button
                                className="gallery-action gallery-action-outline"
                                onClick={() => handleNativeShare(w)}
                                onMouseDown={() => prefetchShareFile(w)}
                                onTouchStart={() => prefetchShareFile(w)}
                                disabled={busyId === w.id}
                                title="Share wallpaper"
                                data-testid={`gallery-native-share-${w.id}`}
                                aria-label="Share wallpaper"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="url(#beta-gradient-stroke)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="18" cy="5" r="3" />
                                  <circle cx="6" cy="12" r="3" />
                                  <circle cx="18" cy="19" r="3" />
                                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                                </svg>
                              </button>
                              <button
                                className="gallery-action gallery-action-outline"
                                onClick={() => handleDownload(w)}
                                disabled={busyId === w.id}
                                title="Download (clean — your copy)"
                                data-testid={`gallery-download-${w.id}`}
                                aria-label="Download wallpaper"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="url(#beta-gradient-stroke)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="7 10 12 15 17 10" />
                                  <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {preview && (
          <div className="gallery-preview" onClick={() => setPreview(null)} data-testid="gallery-preview">
            {imageCache[preview.id] ? (
              <img
                src={`data:image/png;base64,${imageCache[preview.id]}`}
                alt="preview"
              />
            ) : (
              <LazyWallpaperImage
                wallpaperId={preview.id}
                alt="preview"
                onLoaded={(b64) => setImageCache(c => ({ ...c, [preview.id]: b64 }))}
              />
            )}
            <button
              className="init-btn"
              style={{ marginTop: "1rem", maxWidth: "16rem" }}
              onClick={(e) => { e.stopPropagation(); handleDownload(preview); }}
              data-testid="gallery-preview-download"
            >
              ⬇️ Save to my phone
            </button>
            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: "0.85rem" }}>
              <SnapShareButton
                shareUrl={`${window.location.origin}/api/share/wallpaper/${preview.id}`}
                label="Share to Snapchat"
                testId="gallery-preview-snap-share"
              />
            </div>
            <p className="gallery-preview-hint">
              On iPhone: open Photos → tap the saved image → Share → "Use as Wallpaper"
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
